import {commonAdminQueries} from '@common/admin/common-admin-queries';
import {mailTemplateQueries} from '@common/admin/settings/pages/email-settings/templates/requests/mail-template-queries';
import {authGuard} from '@common/auth/guards/auth-route';
import {queryClient} from '@common/http/query-client';
import {valueListsQueryOptions} from '@common/http/value-lists';
import {redirect, RouteObject} from 'react-router';
import {Fragment} from 'react/jsx-runtime';

interface Overrides {
  general?: Partial<RouteObject>;
  system?: Partial<RouteObject>;
  uploading?: Partial<RouteObject>;
}

export const commonAdminSettingsRoutes = (
  appRoutes: RouteObject[],
  overrides?: Overrides,
): RouteObject => {
  return {
    path: 'settings',
    handle: {customDashboardLayout: true},
    loader: () => {
      const redirect = authGuard({permission: 'settings.update'});
      if (redirect) return redirect;
      return queryClient.ensureQueryData(commonAdminQueries.settings.index);
    },
    lazy: () => import('@common/admin/settings/admin-settings-page'),
    children: [
      {
        index: true,
        loader: () => redirect('general'),
        element: <Fragment />,
      },
      ...appRoutes,
      {
        path: 'general',
        lazy: () => import('@common/admin/settings/pages/general-settings'),
        ...overrides?.general,
      },
      {
        path: 'themes',
        lazy: () =>
          import(
            '@common/admin/settings/pages/themes-settings/themes-settings-page'
          ),
      },
      {
        path: 'menus',
        lazy: () =>
          import('@common/admin/settings/pages/menu-settings/menu-settings'),
        loader: () =>
          queryClient.ensureQueryData(
            commonAdminQueries.settings.menuEditorConfig(),
          ),
      },
      {
        path: 'localization',
        lazy: () =>
          import('@common/admin/settings/pages/localization-settings'),
        loader: async () =>
          queryClient.ensureQueryData(
            valueListsQueryOptions(['timezones', 'localizations']),
          ),
      },
      {
        path: 'authentication',
        lazy: () =>
          import('@common/admin/settings/pages/authentication-settings'),
      },
      {
        path: 'uploading',
        lazy: () =>
          import(
            '@common/admin/settings/pages/uploading-settings/uploading-settings'
          ),
        ...overrides?.uploading,
      },
      {
        path: 'email',
        loader: () => redirect('/admin/settings/email/outgoing'),
        element: <Fragment />,
      },
      {
        path: 'email/outgoing',
        lazy: () =>
          import(
            '@common/admin/settings/pages/email-settings/outgoing-email/outgoing-email-settings'
          ),
      },
      {
        path: 'email/templates',
        lazy: () =>
          import(
            '@common/admin/settings/pages/email-settings/templates/mail-templates-page'
          ),
        loader: () => queryClient.ensureQueryData(mailTemplateQueries.index()),
      },
      {
        path: 'email/templates/:key',
        lazy: () =>
          import(
            '@common/admin/settings/pages/email-settings/templates/mail-template-editor-page'
          ),
        loader: ({params}) =>
          queryClient.ensureQueryData(
            mailTemplateQueries.get(params.key!),
          ),
      },
      {
        path: 'custom-code',
        lazy: () => import('@common/admin/settings/pages/custom-code-settings'),
      },
      {
        path: 'system',
        lazy: () =>
          import(
            '@common/admin/settings/pages/system-settings/system-settings'
          ),
        ...overrides?.system,
      },
    ],
  };
};
